// Persona Library API Client
import { supabase } from "@/integrations/supabase/client";
import type { 
  LibraryPersona, 
  PersonaFormData, 
  PersonaScope, 
  PersonaShareConfig,
  PersonaAcl,
  OrgUser,
  PersonaWithPermissions,
  PersonaVisibility,
  PersonaPermission
} from "./persona-types";
import type { Json } from "@/integrations/supabase/types";

// Helper to safely cast Json[] to string[]
function toStringArray(arr: Json[] | Json | null | undefined): string[] {
  if (!arr || !Array.isArray(arr)) return [];
  return arr.filter((item): item is string => typeof item === 'string');
}

// Helper to compute permissions for a persona
function computePermissions(
  persona: LibraryPersona, 
  currentUserId: string | undefined,
  acl?: PersonaAcl[]
): PersonaWithPermissions {
  const isOwner = persona.owner_user_id === currentUserId;
  const isEditor = acl?.some(a => a.user_id === currentUserId && a.permission === 'editor') ?? false;
  
  const getShareBadge = (): PersonaWithPermissions['share_badge'] => {
    switch (persona.visibility) {
      case 'template': return 'Template';
      case 'org': return 'Org';
      case 'custom': return 'Shared';
      case 'project': return 'Project';
      default: return 'Private';
    }
  };

  return {
    ...persona,
    can_edit: isOwner || isEditor,
    can_share: isOwner || isEditor,
    can_delete: isOwner,
    is_owner: isOwner,
    share_badge: getShareBadge(),
  };
}

// Fetch personas by scope
export async function fetchPersonas(scope: PersonaScope): Promise<PersonaWithPermissions[]> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  let query = supabase
    .from('personas')
    .select('*')
    .order('updated_at', { ascending: false });

  switch (scope) {
    case 'recommended':
      query = query.eq('visibility', 'template');
      break;
    case 'mine':
      query = query.eq('owner_user_id', user.id);
      break;
    case 'shared':
      // Get personas shared with the user via ACL
      const { data: aclEntries } = await supabase
        .from('persona_acl')
        .select('persona_id')
        .eq('user_id', user.id);
      
      const sharedIds = aclEntries?.map(a => a.persona_id) || [];
      if (sharedIds.length === 0) return [];
      query = query.in('id', sharedIds);
      break;
    case 'org':
      query = query.eq('visibility', 'org');
      break;
  }

  const { data, error } = await query;
  if (error) throw error;

  // Parse JSONB fields and compute permissions
  return (data || []).map(p => {
    const persona: LibraryPersona = {
      id: p.id,
      org_id: p.org_id,
      owner_user_id: p.owner_user_id,
      name: p.name,
      role: p.role,
      context: p.context ?? '',
      visibility: p.visibility,
      created_at: p.created_at,
      updated_at: p.updated_at,
      goals: toStringArray(p.goals as Json[]),
      constraints: toStringArray(p.constraints as Json[]),
      tags: toStringArray(p.tags as Json[]),
    };
    return computePermissions(persona, user.id);
  });
}

// Create a new persona
export async function createPersona(data: PersonaFormData): Promise<LibraryPersona> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const { data: persona, error } = await supabase
    .from('personas')
    .insert({
      owner_user_id: user.id,
      name: data.name,
      role: data.role,
      goals: data.goals as unknown as Json[],
      constraints: data.constraints as unknown as Json[],
      context: data.context,
      tags: data.tags as unknown as Json[],
      visibility: 'private' as PersonaVisibility,
    })
    .select()
    .single();

  if (error) throw error;
  return {
    id: persona.id,
    org_id: persona.org_id,
    owner_user_id: persona.owner_user_id,
    name: persona.name,
    role: persona.role,
    context: persona.context ?? '',
    visibility: persona.visibility,
    created_at: persona.created_at,
    updated_at: persona.updated_at,
    goals: toStringArray(persona.goals as Json[]),
    constraints: toStringArray(persona.constraints as Json[]),
    tags: toStringArray(persona.tags as Json[]),
  };
}

// Update an existing persona
export async function updatePersona(id: string, data: Partial<PersonaFormData>): Promise<LibraryPersona> {
  const updateData: Record<string, unknown> = {};
  if (data.name !== undefined) updateData.name = data.name;
  if (data.role !== undefined) updateData.role = data.role;
  if (data.goals !== undefined) updateData.goals = data.goals;
  if (data.constraints !== undefined) updateData.constraints = data.constraints;
  if (data.context !== undefined) updateData.context = data.context;
  if (data.tags !== undefined) updateData.tags = data.tags;

  const { data: persona, error } = await supabase
    .from('personas')
    .update(updateData)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return {
    id: persona.id,
    org_id: persona.org_id,
    owner_user_id: persona.owner_user_id,
    name: persona.name,
    role: persona.role,
    context: persona.context ?? '',
    visibility: persona.visibility,
    created_at: persona.created_at,
    updated_at: persona.updated_at,
    goals: toStringArray(persona.goals as Json[]),
    constraints: toStringArray(persona.constraints as Json[]),
    tags: toStringArray(persona.tags as Json[]),
  };
}

// Delete a persona
export async function deletePersona(id: string): Promise<void> {
  const { error } = await supabase
    .from('personas')
    .delete()
    .eq('id', id);

  if (error) throw error;
}

// Duplicate a persona (e.g., from template to user-owned)
export async function duplicatePersona(id: string): Promise<LibraryPersona> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  // Fetch the original persona
  const { data: original, error: fetchError } = await supabase
    .from('personas')
    .select('*')
    .eq('id', id)
    .single();

  if (fetchError) throw fetchError;

  // Create a copy
  const { data: copy, error: createError } = await supabase
    .from('personas')
    .insert({
      owner_user_id: user.id,
      name: `${original.name} (Copy)`,
      role: original.role,
      goals: original.goals,
      constraints: original.constraints,
      context: original.context,
      tags: original.tags,
      visibility: 'private' as PersonaVisibility,
    })
    .select()
    .single();

  if (createError) throw createError;
  return {
    id: copy.id,
    org_id: copy.org_id,
    owner_user_id: copy.owner_user_id,
    name: copy.name,
    role: copy.role,
    context: copy.context ?? '',
    visibility: copy.visibility,
    created_at: copy.created_at,
    updated_at: copy.updated_at,
    goals: toStringArray(copy.goals as Json[]),
    constraints: toStringArray(copy.constraints as Json[]),
    tags: toStringArray(copy.tags as Json[]),
  };
}

// Share a persona - update visibility and ACL
export async function sharePersona(id: string, config: PersonaShareConfig): Promise<void> {
  const { error: updateError } = await supabase
    .from('personas')
    .update({ visibility: config.visibility })
    .eq('id', id);

  if (updateError) throw updateError;

  // If custom visibility, update ACL
  if (config.visibility === 'custom' && config.user_permissions) {
    // First, remove existing ACL entries
    await supabase
      .from('persona_acl')
      .delete()
      .eq('persona_id', id);

    // Then insert new entries
    if (config.user_permissions.length > 0) {
      const { error: aclError } = await supabase
        .from('persona_acl')
        .insert(
          config.user_permissions.map(up => ({
            persona_id: id,
            user_id: up.user_id,
            permission: up.permission,
          }))
        );

      if (aclError) throw aclError;
    }
  }

  // Handle project sharing
  if (config.visibility === 'project' && config.project_ids) {
    // Remove existing project associations
    await supabase
      .from('project_personas')
      .delete()
      .eq('persona_id', id);

    // Add new project associations
    if (config.project_ids.length > 0) {
      const { error: projectError } = await supabase
        .from('project_personas')
        .insert(
          config.project_ids.map(projectId => ({
            persona_id: id,
            project_id: projectId,
          }))
        );

      if (projectError) throw projectError;
    }
  }
}

// Get ACL for a persona using secure database function
export async function getPersonaAcl(personaId: string): Promise<PersonaAcl[]> {
  // Use database function that safely joins profiles via SECURITY DEFINER
  const { data, error } = await supabase
    .rpc('get_persona_acl_with_profiles', { p_persona_id: personaId });

  if (error) throw error;

  return (data || []).map((row: {
    id: string;
    persona_id: string;
    user_id: string;
    permission: PersonaPermission;
    created_at: string;
    user_email: string | null;
    user_name: string | null;
  }) => ({
    id: row.id,
    persona_id: row.persona_id,
    user_id: row.user_id,
    permission: row.permission,
    created_at: row.created_at,
    user_email: row.user_email ?? undefined,
    user_name: row.user_name ?? undefined,
  }));
}

// Update ACL entry permission
export async function updateAclPermission(
  personaId: string, 
  userId: string, 
  permission: PersonaPermission
): Promise<void> {
  const { error } = await supabase
    .from('persona_acl')
    .update({ permission })
    .eq('persona_id', personaId)
    .eq('user_id', userId);

  if (error) throw error;
}

// Remove user from ACL
export async function removeFromAcl(personaId: string, userId: string): Promise<void> {
  const { error } = await supabase
    .from('persona_acl')
    .delete()
    .eq('persona_id', personaId)
    .eq('user_id', userId);

  if (error) throw error;
}

// Add user to ACL
export async function addToAcl(
  personaId: string, 
  userId: string, 
  permission: PersonaPermission
): Promise<void> {
  const { error } = await supabase
    .from('persona_acl')
    .insert({
      persona_id: personaId,
      user_id: userId,
      permission,
    });

  if (error) throw error;
}

// Fetch org users for sharing picker using secure database function
export async function fetchOrgUsers(): Promise<OrgUser[]> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  // Use database function that only returns profiles of org members
  const { data, error } = await supabase
    .rpc('get_org_members_profiles');

  if (error) throw error;

  return (data || []).map((p: {
    id: string;
    email: string | null;
    full_name: string | null;
    avatar_url: string | null;
  }) => ({
    id: p.id,
    email: p.email || '',
    full_name: p.full_name,
    avatar_url: p.avatar_url,
  }));
}

// Attach personas to a run
export async function attachPersonasToRun(runId: string, personaIds: string[]): Promise<void> {
  if (personaIds.length === 0) return;

  const { error } = await supabase
    .from('run_personas')
    .insert(
      personaIds.map(personaId => ({
        run_id: runId,
        persona_id: personaId,
      }))
    );

  if (error) throw error;
}

// Get personas for a run
export async function getRunPersonas(runId: string): Promise<LibraryPersona[]> {
  const { data: runPersonas, error: rpError } = await supabase
    .from('run_personas')
    .select('persona_id')
    .eq('run_id', runId);

  if (rpError) throw rpError;
  if (!runPersonas || runPersonas.length === 0) return [];

  const personaIds = runPersonas.map(rp => rp.persona_id);
  const { data: personas, error: pError } = await supabase
    .from('personas')
    .select('*')
    .in('id', personaIds);

  if (pError) throw pError;

  return (personas || []).map(p => ({
    id: p.id,
    org_id: p.org_id,
    owner_user_id: p.owner_user_id,
    name: p.name,
    role: p.role,
    context: p.context ?? '',
    visibility: p.visibility,
    created_at: p.created_at,
    updated_at: p.updated_at,
    goals: toStringArray(p.goals as Json[]),
    constraints: toStringArray(p.constraints as Json[]),
    tags: toStringArray(p.tags as Json[]),
  }));
}
